import { inject, Injectable, Injector } from '@angular/core';
import { AppGlobalActions } from '@app/_store/actions/global.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EMPTY, map, mergeMap, switchMap, timer } from 'rxjs';

import { PaceActions } from './pace.actions';
import { selectPaceObject } from './pace.selectors';

/** The spec fixes both numbers: re-ask every 10 s, give up after 30 tries. */
export const PACE_POLL_INTERVAL_MS = 10_000;
export const PACE_POLL_MAX_ATTEMPTS = 30;

@Injectable()
export class PaceEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private injector = inject(Injector);

  /**
   * A6. UI-orchestration command (see BudgetsEffects.addBudget$): the plan
   * feature dispatches this instead of importing the accounts feature. The
   * dynamic import keeps the accounts modal in its lazy chunk.
   */
  connectAccount$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PaceActions.connectAccount),
        mergeMap(async () => {
          const { AccountsManageModalsService } =
            await import('@app/features/accounts/_services/accounts-manage-modals.service');
          this.injector
            .get(AccountsManageModalsService)
            .linkExternalAccount('account', 'Monthly Outlook');
        })
      ),
    { dispatch: false }
  );

  /**
   * Recalculating pace is a background job on the server: while it runs, the
   * container arrives with the cached pace and `isUpdating: true`, and the
   * client is expected to keep re-asking for the `pace` container alone until
   * a response carries `isUpdating: false`.
   *
   * Reacting to `loadAppDataSuccess` (rather than driving a loop) makes every
   * poll response — and any unrelated load that happens to refresh pace —
   * re-evaluate the same condition, and `switchMap` keeps at most one timer
   * pending. A failed poll dispatches no success, so the chain simply stops —
   * which is the spec's error behavior. Logout clears the store, the selector
   * yields null, and the counter resets.
   */
  private pollAttempts = 0;

  pollWhileUpdating$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppGlobalActions.loadAppDataSuccess),
      concatLatestFrom(() => this.store.select(selectPaceObject)),
      switchMap(([, pace]) => {
        if (!pace?.isUpdating) {
          this.pollAttempts = 0;
          return EMPTY;
        }

        if (this.pollAttempts >= PACE_POLL_MAX_ATTEMPTS) {
          if (this.pollAttempts === PACE_POLL_MAX_ATTEMPTS) {
            this.pollAttempts += 1;
            console.error(
              'Pace recalculation polling timed out after 30 attempts — the card keeps showing the cached pace until the next data load'
            );
          }
          return EMPTY;
        }

        this.pollAttempts += 1;
        return timer(PACE_POLL_INTERVAL_MS).pipe(
          map(() => AppGlobalActions.loadAppData({ fields: { pace: {} } }))
        );
      })
    )
  );
}
